<script setup>
import Grid from "../../../../frontend/src/components/Grid.vue";
import useGrid from "@/composables/useGrid";
import Vue, {onMounted, ref} from "vue";
import errorReportsService from "@/services/error-reports.service";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import VueMultiselect from "vue-multiselect";
import Editor from "../../../../frontend/src/components/Editor.vue";
import defaults from "../../../../frontend/src/constants/defaults";

const statusOptions = [
    {id: 'new', name: 'New'},
    {id: 'in_progress', name: 'In Progress'},
    {id: 'resolved', name: 'Resolved'}
];

const {gridParams, gridMethods, items} = useGrid(getList);
const filters = ref([]);
const currentId = ref(null);

const showStatusModal = ref(false);
const statusLoading = ref(false);
const newStatus = ref(null);

const showEmailModal = ref(false);
const emailLoading = ref(false);
const emailSubject = ref("");
const emailMessage = ref("");

const columns = [
    {
        label: 'Created',
        name: 'created_at',
    },
    {
        label: 'Startup',
        name: 'startup',
        filter: 'text',
        component: 'RawColumn',
    },
    {
        label: 'Ecosystem',
        name: 'ecosystem',
        filter: 'text',
        component: 'RawColumn',
    },
    {
        label: 'Message',
        name: 'message',
        filter: 'text',
    },
    {
        label: 'Status',
        name: 'status',
        filter: 'single-select',
        options: [
            {
                id: 'new',
                name: 'New'
            },
            {
                id: 'resolved',
                name: 'Resolved',
            },
        ]
    },
    {
        label: 'Status Date',
        name: 'status_date',
    },
    {
        label: 'Sender User',
        name: 'sender_email',
        filter: 'text',
    },
    {
        label: 'Sender Enterprise',
        name: 'sender_enterprise_name',
        filter: 'text',
    },
    {
        label: 'Sender Ecosystem',
        name: 'sender_ecosystem_name',
        filter: 'text',
    },
    {
        name: 'actions',
        label: 'Actions',
        component: 'Actions',
    }
];

onMounted(() => {
    gridMethods.setColumns(columns);
    gridMethods.resetList();
});

function changeFilters(newFilters) {
    filters.value = newFilters;
    gridMethods.resetList();
}

function getList(pageParams) {
    return errorReportsService.getReports(pageParams, filters.value);
}

async function gridAction({action, rowId}) {
    if (action === 'change-status') {
        currentId.value = rowId;

        let currentStatus = items.value.find(item => item.id === rowId).status;
        newStatus.value = statusOptions.find(option => option.id === currentStatus);
        showStatusModal.value = true;
    }
}

async function saveStatus() {
    await errorReportsService.saveStatus(currentId.value, newStatus.value.id);

    showStatusModal.value = false;
    gridMethods.updateList();

    if (newStatus.value.id === 'resolved') {
        let rowData = items.value.find(item => item.id === currentId.value);

        emailSubject.value = `Your reported issue on ${rowData.name} profile has been resolved`;

        emailMessage.value = `Hello ${rowData.sender_name || '___NO_NAME___'},<br>
<br>
Thank you for helping us improve the accuracy of our database.<br>
<br>
We wanted to inform you that the issue you reported regarding the company profile for ${rowData.link} has been reviewed and corrected by our analyst.<br>
<br>
Here is a summary of the update made:<br>
-------Write the details of the update here-------<br>
<br>
If you have any further questions or notice other discrepancies, please don't hesitate to let us know.<br>
<br>
Thank you again for your valuable contribution!<br>
<br>
Best regards,<br>
The SwitchPitch Team`;

        showEmailModal.value = true;
    }
}

async function sendEmail() {
    emailLoading.value = true;

    const {data} = await errorReportsService.sendEmail(
        currentId.value, {
            subject: emailSubject.value,
            message: emailMessage.value
        }
    );

    if (data) {
        emailLoading.value = false;
        showEmailModal.value = false;

        Vue.notify({
            group: 'app',
            type: 'success',
            duration: 1000,
            text: 'Email Sent'
        })
    } else {
        alert("Failed to send email.");
    }

    emailLoading.value = false;
}
</script>

<template>
    <div>
        <div class="main-table-wrapper overflow-auto">
            <div class="main-table">
                <Grid
                    :columns="columns"
                    :data="items"
                    :has-filters="true"
                    :page-length="gridParams.pageLength"
                    :total-rows-count="gridParams.totalRowsCount"
                    :sort="gridParams.sort"
                    :currentPage="gridParams.currentPage"
                    @changePage="gridMethods.changePage"
                    @changeFilters="changeFilters"
                    @action="gridAction"
                >
                </Grid>
            </div>
        </div>

        <b-modal v-model="showStatusModal" title="Change Status">
            <VueMultiselect
                v-model="newStatus"
                placeholder="Select Status"
                :options="statusOptions"
                select-label=""
                deselect-label=""
                label="name"
                track-by="name"
                class="multiselect-mid pl-2"
            />

            <template #modal-footer>
                <PrimaryButton
                    :disabled="!newStatus"
                    caption="Save"
                    loading-caption="Saving..."
                    :loading="statusLoading"
                    @click="saveStatus"
                />
            </template>
        </b-modal>

        <b-modal v-model="showEmailModal" title="Send Email" modal-class="modal-w-lg" no-enforce-focus>
            <form class="form">
                <div class="form__item">
                    <h5 class="heading5 heading5--secondary">Subject *</h5>
                    <input class="form-control--mid form-control" type="text" v-model="emailSubject">
                </div>

                <div class="form__item">
                    <h5 class="heading5 heading5--secondary">Message *</h5>
                    <Editor
                        v-model="emailMessage"
                        :apiKey="apiKeys.tinyMCE"
                        :init="defaults.editorOptions"
                    />
                </div>
            </form>

            <template #modal-footer>
                <PrimaryButton
                    caption="Send"
                    loading-caption="Sending..."
                    :disabled="!emailSubject.length || !emailMessage.length || emailMessage.includes('Write the details of the update here') || emailMessage.includes('NO_NAME')"
                    :loading="emailLoading"
                    @click="sendEmail"
                />
            </template>
        </b-modal>
    </div>
</template>

<style scoped>

</style>
